import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Icon } from '../../../atoms'

const InputContainer = styled.div`
  position: relative;
  display: inline-flex;
  width: 100%;
`

const StyledInput = styled.input<{
  $hasOverride?: boolean
  $isPending?: boolean
}>`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  border: 1.5px solid
    ${({ $hasOverride, $isPending, theme }) =>
      $hasOverride
        ? '#00B341'
        : $isPending
          ? theme.colors.primary
          : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ $hasOverride, $isPending, theme }) =>
    $isPending
      ? theme.colors.primary
      : $hasOverride
        ? '#00B341'
        : theme.colors.text.primary};
  font-weight: ${({ $hasOverride, $isPending }) =>
    $hasOverride || $isPending ? '600' : '400'};

  /* Remove number spinners */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.background.secondary};
    cursor: not-allowed;
  }
`

const ResetBadge = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 1.5px solid white;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: scale(1.1);
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

interface EditableMetricInputProps {
  metricKey: string
  value?: number
  isOverridden: boolean
  isPending: boolean
  /** Key suffix appended to metricKey (default: '_max') */
  suffix?: string
  onValueChange: (metricKey: string, value: number) => void
  onValueClear: (metricKey: string) => void
}

/**
 * Editable input for metric values with local state management.
 * Uses local string state so the user can clear the field (show empty)
 * without the input snapping back to the original value.
 * No number spinners. Reset badge appears as corner icon when overridden.
 */
export const EditableMetricInput: React.FC<EditableMetricInputProps> = ({
  metricKey,
  value,
  isOverridden,
  isPending,
  suffix = '_max',
  onValueChange,
  onValueClear,
}) => {
  const fullKey = `${metricKey}${suffix}`
  const [localValue, setLocalValue] = useState(
    value !== undefined ? String(value) : ''
  )
  // Track if user just cleared so we don't re-sync from prop
  const userClearedRef = useRef(false)

  // Sync local state from prop when value changes externally
  useEffect(() => {
    if (userClearedRef.current) return
    setLocalValue(value !== undefined ? String(value) : '')
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setLocalValue(raw)

    if (raw === '') {
      userClearedRef.current = true
      onValueClear(fullKey)
      return
    }

    userClearedRef.current = false
    const num = parseFloat(raw)
    if (!isNaN(num)) {
      onValueChange(fullKey, num)
    }
  }

  const handleReset = () => {
    userClearedRef.current = false
    onValueClear(fullKey)
  }

  const handleBlur = () => {
    // Reset the cleared flag on blur so future prop changes sync again
    userClearedRef.current = false
  }

  return (
    <InputContainer>
      <StyledInput
        type="number"
        step="0.01"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        $hasOverride={isOverridden}
        $isPending={isPending}
        placeholder="0.00"
      />
      {(isOverridden || isPending) && (
        <ResetBadge
          type="button"
          onClick={handleReset}
          title="Reset to default"
        >
          <Icon name="undo" size="small" />
        </ResetBadge>
      )}
    </InputContainer>
  )
}
